import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { getOrderMonthlyCount } from '../../Redux/features/order/orderSlice';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

const BarChartComponent = () => {
	const dispatch = useDispatch();
	const { monthlyOrderCounts, isLoading } = useSelector((state) => state.order);
	const [InitialFetch, setInitialFetch] = useState(false);
	const [BarChartData, setBarChartData] = useState([]);
	const [Filter, setFilter] = useState({
		DateFrom: '',
		DateTo: '',
	});

	const currentDate = new Date();
	const endDate = currentDate.toISOString().split('T')[0]; // Format to YYYY-MM-DD

	// Set `startDate` to 7 days before the current date
	const startDate = new Date();
	startDate.setDate(currentDate.getDate() - 7); // Subtract 7 days
	const formattedStartDate = startDate.toISOString().split('T')[0]; // Format to YYYY-MM-DD

	// Initial fetch only if there is no data in the redux store
	useEffect(() => {
		// Check if there's no data in the Redux store and no previous fetch has been made
		if (monthlyOrderCounts?.length === 0 && !isLoading && !InitialFetch) {
			const date = {
				startDate: formattedStartDate,
				endDate: endDate,
			};

			// Fetch the data for the last 7 days if it's not already fetched
			dispatch(getOrderMonthlyCount(date)).then((response) => {
				if (response.payload.success === true) {
					setBarChartData(response.payload.monthlyOrderCounts);
					setInitialFetch(true);
				}
			});
		} else {
			// If data already exists in the Redux store, use it
			setBarChartData(monthlyOrderCounts);
		}

		// Set initial filter values if not already set by the user
		if (!Filter.DateFrom || !Filter.DateTo) {
			setFilter({
				DateFrom: formattedStartDate,
				DateTo: endDate,
			});
		}
	}, [
		dispatch,
		formattedStartDate,
		endDate,
		monthlyOrderCounts,
		monthlyOrderCounts?.length,
		isLoading,
		InitialFetch,
		Filter.DateFrom, // Add Filter.DateFrom to avoid overwriting after user selection
		Filter.DateTo, // Add Filter.DateTo to avoid overwriting after user selection
	]);

	// Handle Search button click
	const handleSearch = () => {
		if (Filter.DateFrom && Filter.DateTo) {
			const date = {
				startDate: Filter.DateFrom,
				endDate: Filter.DateTo,
			};
			dispatch(getOrderMonthlyCount(date)).then((response) => {
				if (response.payload.success === true) {
					setBarChartData(response.payload.monthlyOrderCounts);
				}
			});
		} else {
			toast.error('Please select both From Date and To Date.');
		}
	};

	// Handle Reset button click
	const handleReset = () => {
		// Reset the filter to default values (last 7 days range)
		setFilter({
			DateFrom: formattedStartDate,
			DateTo: endDate,
		});
		// Fetch the default data (for the last 7 days)
		const date = {
			startDate: formattedStartDate,
			endDate: endDate,
		};
		dispatch(getOrderMonthlyCount(date)).then((response) => {
			if (response.payload.success === true) {
				setBarChartData(response.payload.monthlyOrderCounts);
			}
		});
	};

	// Reusable handler for setting filter values
	const handleFilterChange = (e) => {
		const { name: fieldName, value: fieldValue } = e.target;

		setFilter({
			...Filter,
			[fieldName]: fieldValue,
		});

		// Perform validation after change
		if (fieldName === 'DateFrom' || fieldName === 'DateTo') {
			validateDates(Filter.DateFrom, Filter.DateTo, fieldName, fieldValue);
		}
	};

	const validateDates = (fromDate, toDate, changedField, newValue) => {
		if (changedField === 'DateFrom') {
			if (newValue && toDate && new Date(newValue) > new Date(toDate)) {
				toast.error('From Date cannot be later than To Date.');
			}
		}

		if (changedField === 'DateTo') {
			if (newValue && fromDate && new Date(newValue) < new Date(fromDate)) {
				toast.error('To Date cannot be earlier than From Date.');
			}
		}
	};

	// Calculate dynamic width based on number of months in the data
	const dynamicChartWidth = BarChartData.length * 150; // 100px for each month's bar

	return (
		<div>
			{/* Conditionally render the Filter section only when data is available */}

			<div className='flex sm:flex-row flex-col gap-3 items-center space-x-4 mb-4 justify-center'>
				<div className='flex flex-row gap-3 max-[440px]:flex-col'>
					<div>
						<input
							type='date'
							name='DateFrom'
							placeholder='From Date'
							className='p-2 border rounded'
							value={Filter.DateFrom}
							onChange={handleFilterChange}
						/>
					</div>
					<div>
						<input
							type='date'
							name='DateTo'
							placeholder='To Date'
							className='p-2 border rounded'
							value={Filter.DateTo}
							onChange={handleFilterChange}
						/>
					</div>
				</div>
				<div className='flex flex-row gap-3'>
					<button
						type='button'
						onClick={handleSearch} // Call the handleSearch function on click
						className='bg-red-500 text-white px-4 py-2 rounded'>
						Search
					</button>

					<button
						type='button'
						onClick={handleReset} // Call the handleReset function on click
						className='bg-red-500 text-white px-4 py-2 rounded'>
						Reset
					</button>
				</div>
			</div>

			{/* Conditionally render the "Orders Status BarChart" title */}
			{BarChartData.length > 0 && (
				<h2 className='text-base font-bold mb-2 flex justify-center uppercase'>
					Orders Status BarChart
				</h2>
			)}

			<div className='flex justify-center items-center p-2 '>
				<div className='max-lg:w-[700px] max-sm:w-[380px] max-[400px]:w-[300px] max-[300px]:w-[250px] w-[80%] flex justify-center'>
					<div className='overflow-x-auto custom-scrollbar'>
						{/* Conditionally render BarChart */}
						{BarChartData.length === 0 ? (
							<div className=' text-red-500'>
								No data available from {Filter.DateFrom} to {Filter.DateTo}
							</div>
						) : (
							<BarChart
								width={dynamicChartWidth || 500}
								height={420}
								margin={5}
								data={BarChartData}>
								<CartesianGrid strokeDasharray='3 3' />
								<XAxis dataKey='date' />
								<YAxis />
								<Tooltip />
								<Bar dataKey='totalOrders' fill='#FF6565' name='Order' />
								<Bar dataKey='pendingOrders' fill='#FFC107' name='Pending' />
								<Bar dataKey='completedOrders' fill='#4CAF50' name='Completed' />
								<Bar dataKey='cancelledOrders' fill='#F44336' name='Cancelled' />
							</BarChart>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default BarChartComponent;
